/**
 * RmsSafetyInfoDialog.js
 *
 * Shown to the person filling in an RMS referral form the moment they give a
 * safety answer that indicates risk (RMS-314). It carries the same content as
 * the companion email so the help is on screen straight away, without anyone
 * having to check an inbox.
 *
 * Modelled on the assessment platform's SelfHarmCrisisSupportDialog, agreed as
 * the pattern on 14 Sep 2026:
 *   - Radix AlertDialog, so neither Escape nor a click outside dismisses it.
 *   - No close button. The only way on is the acknowledge button.
 *   - Presentational only: the email is already away before this opens.
 *
 * It differs from the assessment dialog in two ways, both deliberate:
 *   - the immediate-action line differs for a teacher and a parent, because a
 *     teacher has a safeguarding procedure to follow and a parent does not;
 *   - the child's name is used, per the RMS personalisation rules.
 */

import React from 'react';
import * as AlertDialog from '@radix-ui/react-alert-dialog';
import {
  SAFETY_TITLE,
  SAFETY_NOT_MONITORED,
  SAFETY_IMMEDIATE_ACTION,
  SAFETY_NOT_URGENT_SERVICE,
  EMERGENCY_SERVICES,
  LOCAL_SERVICES,
  SELF_HELP_LINKS,
} from '../utils/safetySignposting';

function ServiceList({ heading, services }) {
  return (
    <>
      <p className="font-bold text-base mt-6">{heading}</p>
      <ul className="list-disc pl-5 space-y-2">
        {services.map((service) => (
          <li key={service.name}>
            <span className="font-semibold">{service.name}</span>
            {': '}
            <span className="font-bold text-[#2980B9]">{service.contact}</span>
            {service.detail ? <span className="block">{service.detail}</span> : null}
          </li>
        ))}
      </ul>
    </>
  );
}

/**
 * @param {object} props
 * @param {boolean} props.isOpen
 * @param {() => void} props.onAcknowledge - called when the reader continues
 * @param {string} props.childName - the child's first name
 * @param {'guardian'|'school_user'} props.respondentRole
 */
export default function RmsSafetyInfoDialog({ isOpen, onAcknowledge, childName, respondentRole }) {
  const name = childName || 'your child';
  const immediateAction =
    (SAFETY_IMMEDIATE_ACTION[respondentRole] || SAFETY_IMMEDIATE_ACTION.guardian)(name);

  return (
    <AlertDialog.Root open={isOpen}>
      <AlertDialog.Portal>
        <AlertDialog.Overlay className="radix-dialog-overlay-motion fixed inset-0 bg-black/60 z-50 backdrop-blur-sm" />
        {/* The services list is long, so only the body scrolls: the title stays
            in view and Continue sits in a sticky bar built as a sibling below
            the scroll area, never reached by scrolling. */}
        <AlertDialog.Content className="radix-dialog-content-centered-motion fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white rounded-2xl shadow-2xl w-[95vw] sm:w-[90vw] max-w-2xl max-h-[90vh] flex flex-col z-50">
          <AlertDialog.Title className="text-lg font-semibold text-[#2A1A28] px-6 sm:px-8 pt-6 sm:pt-8 pb-4 shrink-0">
            {SAFETY_TITLE}
          </AlertDialog.Title>

          <AlertDialog.Description asChild>
            <div className="text-[#2A1A28] text-base leading-relaxed space-y-4 px-6 sm:px-8 flex-1 min-h-0 overflow-y-auto">
              <p className="font-semibold">{SAFETY_NOT_MONITORED}</p>
              <p>{immediateAction}</p>

              <ServiceList heading="If you need help now" services={EMERGENCY_SERVICES} />
              <ServiceList heading="Local services" services={LOCAL_SERVICES} />

              <p className="font-bold text-base mt-6">Self-help</p>
              <ul className="list-disc pl-5 space-y-2">
                {SELF_HELP_LINKS.map((link) => (
                  <li key={link}>{link}</li>
                ))}
              </ul>

              <p>{SAFETY_NOT_URGENT_SERVICE}</p>

              <p>
                We have also emailed this information to you, so you have it to hand.
              </p>
            </div>
          </AlertDialog.Description>

          {/* Gold fill with a plum label: a service-user surface. */}
          <div className="px-6 sm:px-8 py-4 flex justify-end shrink-0 border-t border-[#E6DFE6]">
            <AlertDialog.Action asChild>
              <button
                type="button"
                onClick={onAcknowledge}
                className="inline-flex items-center justify-center px-6 py-3 min-h-[44px] rounded-lg bg-[#E7C22F] text-[#43213E] font-semibold text-base transition-colors hover:bg-[#d4b129] focus:outline-none focus:ring-2 focus:ring-[#43213E] focus:ring-offset-2"
              >
                Continue
              </button>
            </AlertDialog.Action>
          </div>
        </AlertDialog.Content>
      </AlertDialog.Portal>
    </AlertDialog.Root>
  );
}
